/**
 * Per-modal analytics session state for step progression and completion.
 *
 * useModalStepAnalytics records the furthest step reached; trackServiceScheduled
 * marks the matching service modal completed; AppModal reads this on close to
 * emit modal_closed with last_step_* and completed.
 */

import { MODAL_ID_TO_SERVICE_TYPE } from '../constants/marketingAnalyticsEvents';
import type { ServiceType } from '../types';

interface ModalAnalyticsSession {
  lastStepNumber: number;
  lastStepName: string;
  completed: boolean;
}

const sessions = new Map<string, ModalAnalyticsSession>();

/**
 * Starts a fresh analytics session when an AppModal opens.
 */
export function beginModalAnalyticsSession(modalId: string): void {
  sessions.set(modalId, {
    lastStepNumber: 0,
    lastStepName: '',
    completed: false,
  });
}

/**
 * Records the furthest step the user reached during this open cycle.
 */
export function recordModalAnalyticsStep(
  modalId: string,
  stepNumber: number,
  stepName: string,
): void {
  const session = sessions.get(modalId);
  if (!session) return;

  if (stepNumber > session.lastStepNumber) {
    session.lastStepNumber = stepNumber;
    session.lastStepName = stepName;
  }
}

/**
 * Marks the modal session completed (service_scheduled or equivalent fired).
 */
export function markModalAnalyticsCompleted(modalId: string): void {
  const session = sessions.get(modalId);
  if (!session) return;
  session.completed = true;
}

/**
 * Maps a completed service to its wizard modal id for abandonment analytics.
 */
export function markModalAnalyticsCompletedForService(serviceType: ServiceType): void {
  for (const [modalId, mappedType] of Object.entries(MODAL_ID_TO_SERVICE_TYPE)) {
    if (mappedType === serviceType) {
      markModalAnalyticsCompleted(modalId);
    }
  }
}

/**
 * Payload for modal_closed when the modal transitions closed.
 */
export function getModalAnalyticsClosePayload(modalId: string): {
  lastStepNumber: number;
  lastStepName: string;
  completed: boolean;
} {
  const session = sessions.get(modalId);
  return {
    lastStepNumber: session?.lastStepNumber ?? 0,
    lastStepName: session?.lastStepName ?? '',
    completed: session?.completed ?? false,
  };
}

/**
 * Clears session state after modal_closed is emitted.
 */
export function clearModalAnalyticsSession(modalId: string): void {
  sessions.delete(modalId);
}

/** Test-only reset. */
export function __resetModalAnalyticsSessionsForTests(): void {
  sessions.clear();
}
